import * as signalR from "@microsoft/signalr";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { API_BASE_URL } from "../config/api-config";
import logger from "../helpers/logger";
import { authRequest } from "../authConfig";
import { msalInstance } from "../common/interceptor/msal-instance";

/**
 * SignalR service for real-time AI chat streaming.
 * Handles typing indicators, status updates, and streaming token delivery.
 */
class ChatSignalRService {
  // Maximum number of manual connection retry attempts before giving up
  static MAX_RETRY_ATTEMPTS = 5;
  // Delay between retry attempts in milliseconds
  static RETRY_DELAY_MS = 5000;

  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.connectionId = null;
    this.connectionAttempts = 0;
    this._retryTimer = null;
    // Reject handler for cancelling in-flight retry promises
    this._retryReject = null;
    // Promise to track in-flight connection attempts and prevent duplicates
    this._startingPromise = null;
    // Epoch counter and stop flag to handle start/stop race conditions
    this._startEpoch = 0;
    this._stopRequested = false;
    // Async mutex gate for serializing lifecycle transitions
    this._gate = Promise.resolve();
    // AbortController for cancelling in-flight starts
    this._runController = null;
    this.callbacks = {
      onTypingIndicator: [],
      onStatusUpdate: [],
      onTokenChunk: [],
      onFunctionStatus: [],
      onChatError: [],
      onConnected: [],
      onDisconnected: [],
    };
    logger.log(
      `Chat SignalR service initialized. Target URL: ${API_BASE_URL}/chatHub`
    );
  }

  /**
   * Start the SignalR connection to the chat hub
   * @returns {Promise<string>} The connection ID
   */
  async startConnection() {
    // Serialize lifecycle transitions through the async mutex gate
    const previousGate = this._gate;
    let releaseGate;
    this._gate = new Promise((resolve) => {
      releaseGate = resolve;
    });

    try {
      // Wait for any previous lifecycle operation to complete
      await previousGate;

      // If already connected, return existing connectionId
      if (this.connection && this.isConnected) {
        logger.log("Chat SignalR connection already exists");
        return this.connectionId;
      }

      // If a connection start is already in progress, await the same promise
      // to prevent duplicate HubConnection instances and ensure this caller
      // participates in the same async control flow (so releaseGate runs)
      if (this._startingPromise) {
        logger.log("Chat SignalR connection start already in progress, reusing promise");
        const shared = this._startingPromise;
        const result = await shared;
        return result;
      }

      // Clear stop flag for new connection attempt
      this._stopRequested = false;

      // Store the promise so concurrent callers can reuse it
      this._startingPromise = this._doStartConnection();

      try {
        const result = await this._startingPromise;
        return result;
      } finally {
        // Clear the promise on completion (success or failure) so subsequent calls can retry
        this._startingPromise = null;
      }
    } finally {
      // Release the gate for next lifecycle operation
      releaseGate();
    }
  }

  /**
   * Internal method that performs the actual connection logic.
   * @returns {Promise<string>} The connection ID
   * @private
   */
  async _doStartConnection() {
    // Increment epoch and capture for this attempt to detect stale completions
    this._startEpoch++;
    const localEpoch = this._startEpoch;

    // Create AbortController for this connection attempt
    this._runController = new AbortController();
    const signal = this._runController.signal;

    try {
      // Check if already aborted before starting
      if (signal.aborted || this._stopRequested) {
        logger.log("Chat SignalR connection start aborted before beginning");
        throw new Error("Connection start aborted");
      }

      logger.log("Starting Chat SignalR connection...");

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(`${API_BASE_URL}/chatHub`, {
          withCredentials: false,
          transport:
            signalR.HttpTransportType.WebSockets |
            signalR.HttpTransportType.ServerSentEvents |
            signalR.HttpTransportType.LongPolling,
          // Pass JWT token for authentication - required for connection ownership validation
          accessTokenFactory: async () => {
            // Use MSAL best practice: getActiveAccount() with fallback to getAllAccounts()[0]
            // (setActiveAccount is not called in this app, so getActiveAccount() may return null)
            const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
            if (!account) {
              const errorMessage =
                "No active account for chat authentication; anonymous not supported";
              logger.error("Chat SignalR: " + errorMessage);
              throw new Error(errorMessage);
            }

            try {
              const tokenResponse = await msalInstance.acquireTokenSilent({
                ...authRequest,
                account,
              });
              return tokenResponse.accessToken;
            } catch (error) {
              // Check if interaction is required (e.g., consent, MFA, expired session)
              if (error instanceof InteractionRequiredAuthError) {
                // Log the interaction-required error and rethrow so the UI can trigger an interactive flow
                logger.warn(
                  "Chat SignalR: Silent token acquisition failed - interaction required. UI should handle re-authentication.",
                  { errorCode: error.errorCode, errorMessage: error.message }
                );
                throw error;
              }

              // For any other unexpected error, log and re-throw to avoid silent auth failure
              logger.error(
                "Chat SignalR: Unexpected error during token acquisition",
                error
              );
              throw error;
            }
          },
        })
        /**
         * Automatic Reconnection & Token Expiry Behavior
         *
         * .withAutomaticReconnect([0, 2000, 5000, 10000, 30000]) configures SignalR to attempt
         * reconnects at 0ms, 2s, 5s, 10s, and 30s intervals when the connection is lost.
         *
         * IMPORTANT: If the user's auth token has expired (e.g., due to prolonged inactivity),
         * these automatic reconnect attempts will fail authentication. After exhausting all
         * retry intervals, SignalR will transition to a closed state, triggering:
         *   - The `onclose` event handler (see newConnection.onclose below)
         *   - Which invokes all registered `onDisconnected` callbacks
         *
         * Consumers should use the `onDisconnected(callback)` method to:
         *   1. Detect when reconnection has permanently failed
         *   2. Prompt users to re-authenticate (refresh token or re-login)
         *   3. Display appropriate UX for session expiry / prolonged inactivity
         *
         * A successful reconnect requires a valid auth token; re-authentication must occur
         * before calling connect() again.
         */
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Configure timeout settings to match server (KeepAliveInterval=15s, ClientTimeoutInterval=30s)
      // serverTimeoutInMilliseconds should be at least 2x the server's KeepAliveInterval
      newConnection.serverTimeoutInMilliseconds = 60000; // 60 seconds
      newConnection.keepAliveIntervalInMilliseconds = 15000; // 15 seconds (match server)

      // Register event handlers (only once per connection instance)
      newConnection.on("TypingIndicator", (data) => {
        logger.log("Chat SignalR: TypingIndicator", data);
        this.callbacks.onTypingIndicator.forEach((cb) => cb(data));
      });

      newConnection.on("StatusUpdate", (data) => {
        logger.log("Chat SignalR: StatusUpdate", data);
        this.callbacks.onStatusUpdate.forEach((cb) => cb(data));
      });

      newConnection.on("TokenChunk", (data) => {
        // Don't log every token chunk to avoid spam
        this.callbacks.onTokenChunk.forEach((cb) => cb(data));
      });

      newConnection.on("FunctionStatus", (data) => {
        logger.log("Chat SignalR: FunctionStatus", data);
        this.callbacks.onFunctionStatus.forEach((cb) => cb(data));
      });

      newConnection.on("ChatError", (data) => {
        logger.warn("Chat SignalR: ChatError", data);
        this.callbacks.onChatError.forEach((cb) => cb(data));
      });

      // Connection state handlers
      newConnection.onreconnecting((error) => {
        logger.warn("Chat SignalR reconnecting:", error);
        this.isConnected = false;
      });

      newConnection.onreconnected((connectionId) => {
        this.connectionId = connectionId;
        this.isConnected = true;
        logger.log("Chat SignalR reconnected:", connectionId);
        this.callbacks.onConnected.forEach((cb) => cb(connectionId));
      });

      newConnection.onclose((error) => {
        this.isConnected = false;
        this.connectionId = null;
        logger.log("Chat SignalR disconnected", error);
        this.callbacks.onDisconnected.forEach((cb) => cb(error));
      });

      // Start the connection
      await newConnection.start();

      // Verify this attempt is still valid before mutating state
      if (localEpoch !== this._startEpoch || this._stopRequested || signal.aborted) {
        logger.log("Chat SignalR connection start completed but epoch/stop changed, discarding");
        // Stop the connection we just created since it's stale
        try {
          await newConnection.stop();
        } catch (stopErr) {
          logger.warn("Error stopping stale connection:", stopErr);
        }
        throw new Error("Connection start aborted after completion");
      }

      // Safe to mutate state now
      this.connection = newConnection;
      this.isConnected = true;
      this.connectionId = newConnection.connectionId;
      // Reset retry counter on successful connection
      this.connectionAttempts = 0;
      logger.log(`Chat SignalR Connected. ID: ${this.connectionId}`);
      this.callbacks.onConnected.forEach((cb) => cb(this.connectionId));

      return this.connectionId;
    } catch (error) {
      // Handle InteractionRequiredAuthError specially - do NOT increment connectionAttempts
      // or schedule retries. This error requires user interaction (consent, MFA, re-login)
      // and cannot be resolved by retrying silently.
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn(
          "Chat SignalR: InteractionRequiredAuthError caught - skipping retry, requires user interaction",
          { errorCode: error.errorCode, errorMessage: error.message }
        );
        // Mark the error so callers can easily identify it without instanceof checks
        error.isInteractionRequired = true;
        // Rethrow immediately without incrementing attempts or scheduling retry
        throw error;
      }

      this.connectionAttempts++;
      logger.error(
        `Chat SignalR Connection Error (attempt ${this.connectionAttempts}/${ChatSignalRService.MAX_RETRY_ATTEMPTS}):`,
        error
      );

      // Only retry if we haven't exceeded the max attempts
      // SignalR's withAutomaticReconnect handles reconnection after initial connection
      // This manual retry is only for initial connection failures
      if (this.connectionAttempts < ChatSignalRService.MAX_RETRY_ATTEMPTS) {
        // Check if a stop was requested or the start was aborted before scheduling retry
        // This prevents scheduling retries after stopConnection was called, preserving retry budget
        if (
          this._stopRequested === true ||
          this._runController?.signal?.aborted === true
        ) {
          logger.log(
            "Skipping retry scheduling: stop was requested or start was aborted"
          );
          // Clear any pending retry handlers to ensure stopConnection fully prevents further attempts
          if (this._retryTimer) {
            clearTimeout(this._retryTimer);
            this._retryTimer = null;
          }
          if (this._retryReject) {
            this._retryReject = null;
          }
          // Rethrow the error instead of scheduling retry
          throw error;
        }

        logger.log(
          `Will retry Chat SignalR connection in ${ChatSignalRService.RETRY_DELAY_MS / 1000} seconds... (attempt ${this.connectionAttempts + 1}/${ChatSignalRService.MAX_RETRY_ATTEMPTS})`
        );

        // Return a Promise that resolves/rejects based on the retry outcome
        return new Promise((resolve, reject) => {
          // Save reject handler so stopConnection can reject pending promises
          this._retryReject = reject;
          this._retryTimer = setTimeout(async () => {
            this._retryTimer = null;
            this._retryReject = null; // Clear reject handler to avoid leaks
            try {
              const result = await this._doStartConnection();
              resolve(result);
            } catch (retryError) {
              reject(retryError);
            }
          }, ChatSignalRService.RETRY_DELAY_MS);
        });
      } else {
        logger.error(
          `Chat SignalR connection failed after ${ChatSignalRService.MAX_RETRY_ATTEMPTS} attempts. No further retries will be scheduled.`
        );
        // Notify listeners that connection ultimately failed
        const failureError = {
          error: "Connection failed after maximum retry attempts",
          attempts: this.connectionAttempts,
        };
        this.callbacks.onChatError.forEach((cb) => cb(failureError));

        // Reject the promise so callers know the final outcome
        throw new Error(
          `Chat SignalR connection failed after ${ChatSignalRService.MAX_RETRY_ATTEMPTS} attempts`
        );
      }
    }
  }

  /**
   * Stop the SignalR connection
   * @returns {Promise<void>}
   */
  async stopConnection() {
    // Immediately signal stop and abort any in-flight start BEFORE awaiting gate
    // This allows cancelling a hung start without waiting for it to complete
    this._stopRequested = true;
    this._startEpoch++;

    // Abort any in-flight connection attempt immediately
    if (this._runController) {
      this._runController.abort();
      this._runController = null;
    }

    // Serialize lifecycle transitions through the async mutex gate
    const previousGate = this._gate;
    let releaseGate;
    this._gate = new Promise((resolve) => {
      releaseGate = resolve;
    });

    try {
      // Wait for any previous lifecycle operation to complete
      await previousGate;

      // Clear any in-flight connection promise to allow fresh starts after stop
      this._startingPromise = null;

      // Cancel any pending retry timer and reject the pending promise
      if (this._retryTimer) {
        clearTimeout(this._retryTimer);
        this._retryTimer = null;
        if (this._retryReject) {
          this._retryReject(new Error("Retry cancelled by stopConnection"));
          this._retryReject = null;
        }
        logger.log("Cancelled pending Chat SignalR retry timer");
      }

      if (!this.connection) {
        return;
      }

      logger.log("Stopping Chat SignalR connection...");

      // Capture the current connection before clearing instance references
      const conn = this.connection;

      // Clear instance state immediately to prevent concurrent operations
      this.connection = null;
      this.isConnected = false;
      this.connectionId = null;

      try {
        await conn.stop();
        logger.log("Chat SignalR connection stopped successfully");
      } catch (err) {
        logger.error("Error stopping Chat SignalR:", err);
      }
    } finally {
      // Release the gate for next lifecycle operation
      releaseGate();
    }
  }

  /**
   * Reset retry counter to allow fresh connection attempts.
   * Call this before startConnection() if you want to retry after max attempts were reached.
   */
  resetRetryCounter() {
    this.connectionAttempts = 0;
    logger.log("Chat SignalR retry counter reset");
  }

  /**
   * Get the current connection ID (needed for streaming requests)
   * @returns {string|null} The connection ID or null if not connected
   */
  getConnectionId() {
    return this.connectionId;
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  getIsConnected() {
    return this.isConnected;
  }

  // Event registration methods
  onTypingIndicator(callback) {
    if (!this.callbacks.onTypingIndicator.includes(callback)) {
      this.callbacks.onTypingIndicator.push(callback);
    }
  }

  onStatusUpdate(callback) {
    if (!this.callbacks.onStatusUpdate.includes(callback)) {
      this.callbacks.onStatusUpdate.push(callback);
    }
  }

  onTokenChunk(callback) {
    if (!this.callbacks.onTokenChunk.includes(callback)) {
      this.callbacks.onTokenChunk.push(callback);
    }
  }

  onFunctionStatus(callback) {
    if (!this.callbacks.onFunctionStatus.includes(callback)) {
      this.callbacks.onFunctionStatus.push(callback);
    }
  }

  onChatError(callback) {
    if (!this.callbacks.onChatError.includes(callback)) {
      this.callbacks.onChatError.push(callback);
    }
  }

  onConnected(callback) {
    if (!this.callbacks.onConnected.includes(callback)) {
      this.callbacks.onConnected.push(callback);
    }
    // If already connected, call immediately
    if (this.isConnected && this.connectionId) {
      callback(this.connectionId);
    }
  }

  onDisconnected(callback) {
    if (!this.callbacks.onDisconnected.includes(callback)) {
      this.callbacks.onDisconnected.push(callback);
    }
  }

  /**
   * Remove a specific callback
   * @param {string} eventType - The event type (e.g., 'onTokenChunk')
   * @param {Function} callback - The callback to remove
   */
  removeCallback(eventType, callback) {
    if (this.callbacks[eventType]) {
      this.callbacks[eventType] = this.callbacks[eventType].filter(
        (cb) => cb !== callback
      );
    }
  }

  /**
   * Clear all callbacks for a specific event type
   * @param {string} eventType - The event type to clear
   */
  clearCallbacks(eventType) {
    if (this.callbacks[eventType]) {
      this.callbacks[eventType] = [];
    }
  }
}

// Create singleton instance
const chatSignalRService = new ChatSignalRService();

export default chatSignalRService;
